import { Component, AfterViewInit, OnDestroy, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MOYASAR_WALLET_REDIRECT_PATH_SESSION_KEY } from 'src/app/core/data-models/wallet.data.models';

declare var Moyasar: any;

@Component({
  selector: 'app-moyasar-payment',
  templateUrl: './moyasar-payment.component.html',
  styleUrls: ['./moyasar-payment.component.css']
})
export class MoyasarPaymentComponent implements AfterViewInit, OnDestroy {

  @Input() publishableKey = '';
  @Input() cardNetworks: string[] = [];
  @Input() applePayEnabled = false;
  /** When set, the amount step is skipped and the Moyasar form opens straight away for this amount. */
  @Input() presetAmount: number | null = null;
  /** Where Moyasar redirects after payment. Defaults to the desktop wallet callback page. */
  @Input() callbackUrl = '';
  /** Extra Moyasar metadata. Wallet charges pass the wallet id as payment_id. */
  @Input() metadata: Record<string, string> = {};
  amount: number = 0;
   @Input() redirectPathAfterPayment = '/#/Borrower/Wallet';
  private static readonly APPLE_PAY_COUNTRY = 'SA';
  private static readonly APPLE_PAY_VALIDATE_MERCHANT_URL = 'https://api.moyasar.com/v1/applepay/initiate';
  private static readonly DEFAULT_APPLE_PAY_LABEL = 'Tal Finance';
  private scriptLoaded = false;
  private scriptLoading = false;
  isLoading: boolean = false;
 isMoyaserInit :boolean=false;
  constructor(private translate: TranslateService) {}

  ngAfterViewInit(): void {
    this.callbackUrl ||= `${window.location.origin}/wallet-payment-callback.html`;
    
    this.loadMoyasarScript();
    this.loadMoyasarStyles();

    if (this.presetAmount && this.presetAmount > 0) {
      this.amount = this.presetAmount;
      // Deferred so the state changes in initPayment do not happen during this change-detection pass.
      setTimeout(() => this.initPayment());
    }
  }

  private loadMoyasarScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded || (window as any).Moyasar) {
        this.scriptLoaded = true;
        return resolve();
      }

      if (this.scriptLoading) {
        const checkInterval = setInterval(() => {
          if (this.scriptLoaded) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        return;
      }

      this.scriptLoading = true;
      const script = document.createElement('script');
      script.src = 'https://cdn.moyasar.com/mpf/1.7.0/moyasar.js';
      script.onload = () => {
        this.scriptLoaded = true;
        this.scriptLoading = false;
        resolve();
      };
      script.onerror = () => {
        this.scriptLoading = false;
        reject('Failed to load Moyasar script');
      };
      document.body.appendChild(script);
    });
  }

  private loadMoyasarStyles(): void {
    if (!document.getElementById('moyasar-css')) {
      const link = document.createElement('link');
      link.id = 'moyasar-css';
      link.rel = 'stylesheet';
      link.href = 'https://cdn.moyasar.com/mpf/1.7.0/moyasar.css';
      document.head.appendChild(link);
    }
  }

  async initPayment() {
    if (!this.amount || this.amount <= 0) {
      alert(this.translate.instant('wallet.invalidAmount'));
      return;
    }

    const publishableKey = (this.publishableKey ?? '').trim();
    if (!publishableKey.startsWith('pk_')) {
      alert(this.translate.instant('wallet.moyasarUnavailable'));
      return;
    }

    this.isLoading = true;

    try {
      if (!this.scriptLoaded) {
        await this.loadMoyasarScript();
      }

      this.clearExistingForm();

      sessionStorage.setItem(
        MOYASAR_WALLET_REDIRECT_PATH_SESSION_KEY,
        this.redirectPathAfterPayment
      );

      const methods: string[] = [];
      if (this.cardNetworks?.length) {
        methods.push('creditcard');
      }
      if (this.applePayEnabled) {
        methods.push('applepay');
      }
      if (!methods.length) {
        methods.push('creditcard');
      }

      const initOptions: any = {
        element: ".mysr-form",
        // Moyasar expects whole halalas; 19.99 * 100 is 1998.9999… in floating point.
        amount: Math.round(this.amount * 100),
        currency: "SAR",
        description: this.translate.instant('wallet.walletChargeDescription'),
        publishable_api_key: publishableKey,
        callback_url: this.callbackUrl,
        methods: methods,
        metadata: {
          source: 'angular-portal',
          ...this.metadata
        },
        onReady: () => {
          this.isLoading = false;
        },
        onClose: () => {
          this.isLoading = false;
        }
      };

      if (this.cardNetworks?.length) {
        initOptions.supported_networks = this.cardNetworks;
      }

      if (this.applePayEnabled) {
        initOptions.apple_pay = {
          country: MoyasarPaymentComponent.APPLE_PAY_COUNTRY,
          label: MoyasarPaymentComponent.DEFAULT_APPLE_PAY_LABEL,
          validate_merchant_url: MoyasarPaymentComponent.APPLE_PAY_VALIDATE_MERCHANT_URL
        };
      }

      Moyasar.init(initOptions);

          this.isMoyaserInit=true;
    } catch (err) {
      this.isLoading = false;
    }
  }

  private clearExistingForm(): void {
    const existingForm = document.querySelector('.mysr-form');
    if (existingForm) {
      existingForm.innerHTML = '';
    }
  }

  ngOnDestroy(): void {
    this.scriptLoaded = false;
  }
}